import { X } from "@tamagui/lucide-icons";
import { useEffect, useMemo, useState } from "react";

import {
  Button,
  Dialog,
  H1,
  Paragraph,
  Sheet,
  TextArea,
  XStack,
  YStack,
} from "@/components";
import { Footer } from "@/components/Layout";
import { useAnalytics, useEventContext } from "@/hooks/analytics";
import { useCSATSurveyStore } from "@/store/survey";
import {
  type AnswerValue,
  type Answers,
  QUESTION_TYPES,
  type SurveyQuestion,
} from "@/survey/constants";

const RATING_VALUES = [1, 2, 3, 4, 5];
const THANK_YOU_TITLE = "Thank you for your feedback";

const getVisibleQuestions = (
  questions: SurveyQuestion[],
  answers: Answers,
): SurveyQuestion[] => questions.filter((q) => !q.showIf || q.showIf(answers));

const RatingQuestion = ({
  onChange,
  lowLabel,
  highLabel,
}: {
  onChange: (value: number) => void;
  lowLabel: string;
  highLabel: string;
}) => (
  <YStack gap="$150">
    <XStack
      gap="$100"
      justifyContent="space-between"
      flexWrap="wrap"
      $maxLg={{ justifyContent: "center" }}
    >
      {RATING_VALUES.map((rating) => (
        <Button
          key={rating}
          mode="surveyOption"
          onPress={() => onChange(rating)}
          minHeight="$600"
          justifyContent="center"
          flexGrow={1}
        >
          {String(rating)}
        </Button>
      ))}
    </XStack>
    <XStack justifyContent="space-between" gap="$150">
      <Paragraph size="$sm">{lowLabel}</Paragraph>
      <Paragraph size="$sm" textAlign="right">
        {highLabel}
      </Paragraph>
    </XStack>
  </YStack>
);

export default function CSATSurveyModal() {
  const currentSurvey = useCSATSurveyStore((state) => state.currentSurvey);
  const clearSurvey = useCSATSurveyStore((state) => state.clearSurvey);
  const { track } = useAnalytics();
  const { pathname, location } = useEventContext();
  // Keep the last requested survey mounted while `isOpen` transitions to false to preserve animation
  const [renderedSurvey, setRenderedSurvey] = useState(currentSurvey);
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [freeText, setFreeText] = useState("");

  const questions = renderedSurvey?.questions ?? [];
  const surveySteps = useMemo(
    () => getVisibleQuestions(questions, answers),
    [questions, answers],
  );

  useEffect(() => {
    if (currentSurvey) {
      setRenderedSurvey(currentSurvey);
      setStep(0);
      setAnswers({});
      setFreeText("");
      setIsOpen(true);
    }
  }, [currentSurvey]);

  if (!renderedSurvey) {
    return null;
  }

  const { surveyKey, conversationId, encounterId } = renderedSurvey;

  const isDone = step >= surveySteps.length;
  const currentStep = isDone ? null : surveySteps[step];
  const eventContext = {
    pathname,
    location,
    conversationId,
  };

  const trackSubmitted = (finalAnswers: Answers) => {
    track("superclinical-__csat-submitted", {
      surveyKey,
      conversationId,
      encounterId: encounterId ?? undefined,
      answers: finalAnswers,
      event_context: eventContext,
    });
  };

  const finish = (finalAnswers: Answers, totalSteps: number) => {
    trackSubmitted(finalAnswers);
    setStep(totalSteps);
  };

  const advance = (value: AnswerValue) => {
    if (!currentStep) {
      return;
    }

    const updatedAnswers = { ...answers, [currentStep.key]: value };
    setAnswers(updatedAnswers);
    if (currentStep.type === QUESTION_TYPES.FREE_TEXT) {
      setFreeText("");
    }

    const nextSurveySteps = getVisibleQuestions(questions, updatedAnswers);
    if (step === nextSurveySteps.length - 1) {
      finish(updatedAnswers, nextSurveySteps.length);
      return;
    }

    setStep((prev) => prev + 1);
  };

  const handleDismiss = () => {
    if (!isOpen) {
      return;
    }

    if (isDone) {
      setIsOpen(false);
      clearSurvey();
      return;
    }

    const finalAnswers =
      currentStep?.type === QUESTION_TYPES.FREE_TEXT && freeText
        ? { ...answers, [currentStep.key]: freeText }
        : answers;
    track("superclinical-__csat-dismissed", {
      surveyKey,
      conversationId,
      encounterId: encounterId ?? undefined,
      completedSteps: step,
      answers: finalAnswers,
      event_context: eventContext,
    });

    setIsOpen(false);
    clearSurvey();
  };

  const title = isDone ? THANK_YOU_TITLE : currentStep?.text;
  const subtitle = isDone ? undefined : currentStep?.subtitle;

  // HACK: We prefer <Adapt.Contents/> instead of rendering body directly, but
  // Adapt teleports content after mount, which breaks the Sheet's
  // snapPointsMode="fit" height measurement.
  const body = (
    <>
      <XStack alignItems="center" justifyContent="space-between" gap="$150">
        <YStack flex={1} gap="$100">
          <Dialog.Title asChild>
            <H1 size="$lg" $lg={{ size: "$lg" }}>
              {title}
            </H1>
          </Dialog.Title>
          {subtitle && <Paragraph size="$sm">{subtitle}</Paragraph>}
        </YStack>
        <Dialog.Close asChild displayWhenAdapted>
          <Button.Wrapper aria-label="Close survey" flexShrink={0}>
            <X size={20} />
          </Button.Wrapper>
        </Dialog.Close>
      </XStack>

      {currentStep?.type === QUESTION_TYPES.RATING && (
        <RatingQuestion
          lowLabel={currentStep.lowLabel}
          highLabel={currentStep.highLabel}
          onChange={advance}
        />
      )}
      {currentStep?.type === QUESTION_TYPES.FREE_TEXT && (
        <TextArea
          value={freeText}
          onChangeText={setFreeText}
          placeholder="Your feedback (optional)"
          numberOfLines={3}
        />
      )}

      {isDone ? (
        <Footer>
          <Button mode="primary" onPress={handleDismiss}>
            Done
          </Button>
        </Footer>
      ) : currentStep?.type === QUESTION_TYPES.FREE_TEXT ? (
        <Footer>
          <Button mode="primary" onPress={() => advance(freeText || null)}>
            {step === surveySteps.length - 1 ? "Submit" : "Next"}
          </Button>
        </Footer>
      ) : null}
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen: boolean) => !nextOpen && handleDismiss()}
      modal
      disableRemoveScroll
    >
      <Dialog.Adapt when="maxLg">
        <Sheet modal snapPointsMode="fit" disableDrag moveOnKeyboardChange>
          <Sheet.Frame key="frame" padding="$300" gap="$200">
            {body}
          </Sheet.Frame>
          <Sheet.Overlay
            key="overlay"
            backgroundColor="$fillOverlay"
            backdropFilter="blur(8px)"
          />
        </Sheet>
      </Dialog.Adapt>
      <Dialog.Portal>
        <Dialog.Overlay
          key="overlay"
          backgroundColor="$fillOverlay"
          backdropFilter="blur(8px)"
        />
        <Dialog.Content
          key="content"
          width="100%"
          maxWidth={560}
          borderRadius="$200"
          overflow="hidden"
          padding="$300"
          gap="$200"
        >
          {body}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
}
